import { useEffect, useRef, useState } from "react";
import { useQuery, useStatus } from "@powersync/react";
import { getAuthToken, setAuthToken, syncApiUrl, SYNC_REJECTED_EVENT } from "@cliqsuite/db/powersync";
import { connectKds, db } from "./db";
import { readPairedKdsDevice, writePairedKdsDevice, type PairedKdsDevice } from "./kdsDevice";

interface StoreRow {
  id: string;
  name: string;
}

// See apps/pos's useDevicePairing.ts for the full story — this table has no
// store/business filter (every display syncs the whole platform's devices),
// so a reconnect/resync blip can momentarily render this display's own row
// missing, indistinguishable in a single render from a genuine remote
// unpair. A "not paired" reading only takes effect after this delay,
// re-verified against a fresh snapshot rather than the one that first
// tripped it.
//
// Was 15s — raised to match useDevicePairing.ts's own fix, for the same
// reason: this shape has been directly observed taking up to 90-150s to
// resync under real conditions, which silently wiped a display's local
// pairing state well before Owner Portal's "paired" status ever changed.
const UNPAIR_CONFIRM_MS = 180_000;

/**
 * Device pairing gate only — Kitchen Display has no staff identity to track.
 * Anyone at the pass can use a paired display; pairing (which physical
 * display this is) is the only access control that matters here.
 */
export function useKdsSession() {
  const [device, setDevice] = useState<PairedKdsDevice | null>(() => readPairedKdsDevice());
  const [pairError, setPairError] = useState("");
  const [pairing, setPairing] = useState(false);
  const [remoteUnpaired, setRemoteUnpaired] = useState(false);

  // Only used to notice the owner unpairing this display remotely; identity comes from localStorage.
  const { data: devices, isLoading } = useQuery<{ id: string; is_paired: number }>("SELECT id, is_paired FROM devices");
  // Only judge "was I unpaired?" after a real sync; an offline boot has an empty devices table.
  const { hasSynced } = useStatus();
  const devicesReady = !!device && !!hasSynced && !isLoading;
  const devicesRef = useRef(devices);
  devicesRef.current = devices;

  useEffect(() => {
    if (!device || !devicesReady) return;
    const match = (devices ?? []).find((d) => d.id === device.deviceId);
    if (match && match.is_paired) return;
    const timer = setTimeout(() => {
      const latest = devicesRef.current ?? [];
      const stillMatch = latest.find((d) => d.id === device.deviceId);
      if (stillMatch && stillMatch.is_paired) return;
      setRemoteUnpaired(true); // shows "This display was unpaired" instead of silently resetting
      void db.disconnect();
    }, UNPAIR_CONFIRM_MS);
    return () => clearTimeout(timer);
  }, [device, devices, devicesReady]);

  // Same fail-safe as the till: the server refusing this display's token means it must be paired again.
  useEffect(() => {
    if (!device) return;
    const needsPairing = () => {
      setRemoteUnpaired(true);
      void db.disconnect();
    };
    window.addEventListener(SYNC_REJECTED_EVENT, needsPairing);
    return () => window.removeEventListener(SYNC_REJECTED_EVENT, needsPairing);
  }, [device]);

  // Captured once at pairing time (same limitation/behavior local-store.ts's
  // POS equivalent already has) rather than kept live — a store rename on
  // Owner Portal won't retroactively update an already-paired display's
  // cached name until it's re-paired. Acceptable: this is a display label,
  // not something correctness depends on.
  const store: StoreRow | null = device ? { id: device.storeId, name: device.storeName } : null;

  async function pair(codeRaw: string) {
    const code = codeRaw.trim().toUpperCase();
    if (!code) {
      setPairError("Enter a pairing code.");
      return;
    }
    setPairing(true);
    setPairError("");
    try {
      // Server-validated now (not a client-side find-by-code against the
      // whole platform's devices) — see apps/pos's useDevicePairing.ts,
      // the same fix for the same reason. Also mints this display's
      // shape-read scope token (PGlite migration plan, Phase 1b), and
      // returns storeName/registerName directly so nothing here ever needs
      // to read core.devices/core.stores before that token exists.
      const res = await fetch(syncApiUrl("/core/devices/pair"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ code, deviceType: "kds" }),
      });
      const body = (await res.json().catch(() => null)) as
        | {
            ok: true;
            deviceId: string;
            businessId: string;
            storeId: string;
            storeName: string;
            registerName: string;
            scopeToken: string;
          }
        | { error: string }
        | null;
      if (!res.ok || !body || "error" in body) {
        setPairError(
          body && "error" in body && body.error === "invalid_code"
            ? "Code not found. Check the Business app, and that it's a Kitchen Display code."
            : "Couldn't pair — check your connection and try again.",
        );
        return;
      }
      setAuthToken(body.scopeToken);
      const paired: PairedKdsDevice = {
        deviceId: body.deviceId,
        businessId: body.businessId,
        storeId: body.storeId,
        storeName: body.storeName,
        registerName: body.registerName,
        pairingCode: code,
      };
      writePairedKdsDevice(paired);
      setDevice(paired);
      void connectKds();
    } finally {
      setPairing(false);
    }
  }

  async function unpair() {
    try {
      if (device) {
        await fetch(syncApiUrl(`/core/devices/${device.deviceId}`), {
          method: "PATCH",
          headers: { "content-type": "application/json", authorization: `Bearer ${getAuthToken()}` },
          body: JSON.stringify({ is_paired: false, device_name: null }),
        }).catch(() => undefined);
        await db.disconnectAndClear();
      }
    } finally {
      setAuthToken(null);
      writePairedKdsDevice(null);
      window.location.reload();
    }
  }

  return {
    device,
    store,
    pairError,
    pairing,
    pair,
    unpair,
    remoteUnpaired,
  };
}
